import { useState, useCallback, useEffect } from "react";

import { DraftCompleteness } from "./DraftCompleteness";

interface DraftPreviewProps {
  markdown: string;
  onEdit?: (markdown: string) => void;
  onSaveAndReview?: (markdown: string) => void;
  isLoading?: boolean;
}

/**
 * Live preview panel for the concept besluit.
 * Shows the markdown draft with copy/download/edit functionality.
 */
export function DraftPreview({
  markdown,
  onEdit,
  onSaveAndReview,
  isLoading = false,
}: DraftPreviewProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMarkdown, setEditedMarkdown] = useState(markdown);
  const [copied, setCopied] = useState(false);

  // Sync with incoming markdown when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedMarkdown(markdown);
    }
  }, [markdown, isEditing]);

  // Copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(editedMarkdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = editedMarkdown;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [editedMarkdown]);

  // Download as .md file
  const handleDownload = useCallback(() => {
    // Extract title from first heading for filename
    const titleMatch = editedMarkdown.match(/^#\s+(.+)$/m);
    const title = titleMatch?.[1] ?? "besluit";
    const filename = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.md`;

    const blob = new Blob([editedMarkdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [editedMarkdown]);

  // Check if the draft has been modified from the original
  const hasChanges = editedMarkdown !== markdown;

  // Toggle edit mode - save locally when exiting edit mode
  const handleToggleEdit = useCallback(() => {
    if (isEditing && onEdit) {
      onEdit(editedMarkdown);
    }
    setIsEditing(!isEditing);
  }, [isEditing, editedMarkdown, onEdit]);

  // Save and send for AI review
  const handleSaveAndReview = useCallback(() => {
    if (onSaveAndReview) {
      onSaveAndReview(editedMarkdown);
    }
    setIsEditing(false);
  }, [editedMarkdown, onSaveAndReview]);

  // Handle text changes
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedMarkdown(e.target.value);
  }, []);

  // Empty state
  if (!markdown && !isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 bg-surface-container-lowest border-b border-outline-variant">
          <span className="text-sm font-medium text-on-surface-variant">Concept Besluit</span>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-on-surface-variant">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-outline-variant"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-lg font-medium mb-2">Nog geen concept besluit</p>
            <p className="text-sm">
              Start het gesprek links om een besluit op te stellen. De preview verschijnt hier
              terwijl je werkt.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with completeness indicator */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface-container-lowest border-b border-outline-variant flex-shrink-0">
        <DraftCompleteness markdown={editedMarkdown} size={40} />
        {isLoading && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-tertiary-container text-on-tertiary-container">
            <svg
              className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-tertiary"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Bijwerken...
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative group">
        {isEditing ? (
          <div className="h-full p-4 overflow-auto">
            <textarea
              value={editedMarkdown}
              onChange={handleChange}
              className="w-full h-full min-h-[400px] p-4 font-mono text-sm bg-surface-container-lowest border border-outline-variant rounded-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Markdown bewerken..."
            />
          </div>
        ) : (
          <>
            {/* Scrollable content underneath */}
            <div className="h-full p-4 overflow-auto cursor-pointer" onClick={handleToggleEdit}>
              <div className="bg-surface-container-lowest border border-outline-variant rounded-sm p-4">
                <pre className="text-sm font-mono whitespace-pre-wrap text-on-surface-variant">
                  {editedMarkdown}
                </pre>
              </div>
            </div>
            {/* Fixed overlay hint - stays in place while content scrolls */}
            <div
              className="absolute inset-0 bg-surface-container/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none"
              aria-hidden="true"
            >
              <span className="bg-surface-container-lowest px-4 py-2 rounded-md shadow-3 text-on-surface-variant flex items-center gap-2 border border-outline-variant">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Klik om te bewerken
              </span>
            </div>
          </>
        )}
      </div>

      {/* Bottom toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface-container-lowest border-t border-outline-variant flex-shrink-0">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              {/* Cancel edit button */}
              <button
                onClick={() => {
                  setEditedMarkdown(markdown);
                  setIsEditing(false);
                }}
                className="px-3 py-1.5 text-sm bg-surface-container-lowest text-on-surface border border-outline-variant rounded-sm hover:bg-surface-container-low transition-colors"
                title="Annuleren"
              >
                Annuleren
              </button>

              {/* Save and Review button - primary action when editing */}
              {onSaveAndReview && hasChanges && (
                <button
                  onClick={handleSaveAndReview}
                  className="px-3 py-1.5 text-sm bg-primary text-on-primary rounded-sm hover:brightness-110 transition-colors shadow-1"
                  title="Opslaan en feedback vragen aan Akela"
                >
                  <svg
                    className="w-4 h-4 inline-block mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Opslaan & Feedback
                </button>
              )}

              {/* Simple save without review */}
              <button
                onClick={handleToggleEdit}
                className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                  hasChanges
                    ? "bg-surface-container-lowest text-on-surface border border-outline-variant hover:bg-surface-container-low"
                    : "bg-primary text-on-primary hover:brightness-110"
                }`}
                title="Alleen opslaan (zonder feedback)"
              >
                {hasChanges ? "Alleen opslaan" : "Sluiten"}
              </button>
            </>
          ) : (
            <button
              onClick={handleToggleEdit}
              className="px-4 py-2 text-sm bg-primary text-on-primary rounded-sm hover:brightness-110 transition-colors font-medium shadow-1"
              title="Bewerken"
            >
              <svg
                className="w-4 h-4 inline-block mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Bewerken
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => void handleCopy()}
            disabled={!editedMarkdown}
            className="px-3 py-1.5 text-sm bg-surface-container-lowest text-on-surface border border-outline-variant rounded-sm hover:bg-surface-container-low transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Kopiëren naar klembord"
          >
            {copied ? (
              <>
                <svg
                  className="w-4 h-4 inline-block mr-1 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Gekopieerd!
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4 inline-block mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Kopiëren
              </>
            )}
          </button>

          <button
            onClick={handleDownload}
            disabled={!editedMarkdown}
            className="px-3 py-1.5 text-sm bg-surface-container-lowest text-on-surface border border-outline-variant rounded-sm hover:bg-surface-container-low transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Downloaden als .md bestand"
          >
            <svg
              className="w-4 h-4 inline-block mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
